'use client';

import { useAuthProvider } from 'app/_modules/auth/providers/AuthProvider/AuthProvider';
import { useFlagsProxy } from 'app/_modules/feature-flags/hooks/useFlagsProxy';

import { AppState, useAuth0 } from '@auth0/auth0-react';
import { RedirectLoginOptions, User as Auth0User, IdToken } from '@auth0/auth0-spa-js';
import { useRouter } from 'next/navigation';
import { IdTokenClaims, SigninRedirectArgs, User as GenericUser } from 'oidc-client-ts';
import { createContext, useContext, useCallback, FC } from 'react';
import { useAuth } from 'react-oidc-context';
import { parseOemConnection, hasIdpManagedPermissions, hasIdpManagedProfile } from 'shared/src/user/types';

const SHOULD_FORCE_GENERIC_AUTH_LOCALSTORAGE_KEY = 'SHOULD_FORCE_GENERIC_AUTH';
const SHOULD_FORCE_GENERIC_AUTH_TRUTHY_VALUE = '1';
export const checkIfShouldForceGenericAuth = () => window.localStorage.getItem(SHOULD_FORCE_GENERIC_AUTH_LOCALSTORAGE_KEY) === SHOULD_FORCE_GENERIC_AUTH_TRUTHY_VALUE || process.env.AUTH_CLIENT_PROVIDER !== 'auth0';

export type PromptOptions = 'none' | 'login' | 'consent' | 'select_account' | undefined;
export interface LoginWithRedirectOptions {
  organizationId?: string;
  returnTo?: string;
  prompt?: PromptOptions;
  redirect_uri?: string;
  connection?: string;
  utm?: Record<string, string>;
  screen_hint?: string;
  loginHint?: string;
  // arbitrary query parameters to be added to the login redirect URL
  customQueryParams?: Record<string, string>;
}

export type CommonAuthProviderContextValue = {
  isLoading: boolean;
  isAuthenticated: boolean;
  hasIdpManagedProfile: boolean;
  hasIdpManagedPermissions: boolean;
  oemConnection: string | null;
  user?: Auth0User | GenericUser['profile'];
  /**
   * Internal method for logging in. It will redirect user to a 3rd party SSO.
   * Normally you should use the `login` method instead, unless you are sure
   * you need to explicitly redirect to a 3rd party SSO.
   */
  loginWithRedirect: (params?: LoginWithRedirectOptions) => Promise<void>;
  logout: (params: { returnTo: string }) => Promise<void>;
  getAccessToken: (params?: { cacheMode?: 'off' }) => Promise<string | undefined>;
  getIdTokenClaims: () => Promise<IdTokenClaims | IdToken | undefined>;
};

export type CommonAuthProviderContext = CommonAuthProviderContextValue & {
  /**
   * Your go-to method for logging users in. It will check the new login experience flag
   * and then redirect to the appropriate login experience in SSO or redirect
   * user to the new login experience pages in Maestro UI for the Discovery phase.
   */
  login: (params?: LoginWithRedirectOptions) => Promise<void>;
};

const CommonAuthProviderContext = createContext<CommonAuthProviderContextValue>({
  isLoading: false,
  isAuthenticated: false,
  hasIdpManagedProfile: false,
  hasIdpManagedPermissions: false,
  oemConnection: null,
  user: undefined,
  logout: () => { throw new Error('Context not initialized'); },
  loginWithRedirect: () => { throw new Error('Context not initialized'); },
  getAccessToken: () => { throw new Error('Context not initialized'); },
  getIdTokenClaims: () => { throw new Error('Context not initialized'); },
});

function genericAuthExtraQueryParams(params?: LoginWithRedirectOptions) {
  return {
    audience: process.env.AUTH_CLIENT_AUDIENCE_BROWSER as string,
    ...(params?.organizationId && ({ organization: params.organizationId })),
    ...(params?.prompt && ({ prompt: params.prompt })),
    ...(params?.redirect_uri && ({ redirect_uri: params.redirect_uri })),
    ...(params?.connection && ({ idp: params.connection })),
    ...params?.utm,
    ...(params?.screen_hint && { screen_hint: params.screen_hint }),
    ...(params?.loginHint && { login_hint: params.loginHint }),
    ...params?.customQueryParams,
  };
}

export const GenericAuthProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const genericAuth = useAuth();

  const value: CommonAuthProviderContextValue = {
    isLoading: genericAuth.isLoading,
    user: genericAuth.user?.profile,
    isAuthenticated: genericAuth.isAuthenticated,
    // TODO - The preferred_username field in Auth0 is set to an email address. We either need to change that to be
    // consistent with Okta or come up with some new consistently-set profile field before we can use this provider
    // for Auth0
    hasIdpManagedPermissions: hasIdpManagedPermissions(genericAuth.user?.profile?.preferred_username),
    hasIdpManagedProfile: hasIdpManagedProfile(genericAuth.user?.profile?.preferred_username),
    oemConnection: null,
    logout: async (params) => genericAuth.signoutRedirect({ extraQueryParams: { prompt: 'none' }, state: { returnTo: params.returnTo } }),
    getIdTokenClaims: async () => genericAuth.user?.profile,
    getAccessToken: async (params) => {
      if (params?.cacheMode === 'off') {
        return (await genericAuth.signinSilent({ prompt: 'none' }))?.access_token;
      }

      return genericAuth.user?.access_token;
    },
    loginWithRedirect: async (params) => {
      const config: SigninRedirectArgs = {
        state: { returnTo: params?.returnTo },
        extraQueryParams: genericAuthExtraQueryParams(params),
      };

      return genericAuth.signinRedirect(config);
    },
  };

  return (
    <CommonAuthProviderContext.Provider value={value}>
      {children}
    </CommonAuthProviderContext.Provider>
  );
};

function auth0AuthorizationParams(params?: LoginWithRedirectOptions) {
  return {
    ...(params?.organizationId && ({ organization: params.organizationId })),
    ...(params?.prompt && { prompt: params.prompt }),
    ...(params?.redirect_uri && { redirect_uri: params.redirect_uri }),
    ...(params?.connection && { connection: params.connection }),
    ...params?.utm,
    ...(params?.screen_hint && { screen_hint: params.screen_hint }),
    ...(params?.loginHint && { login_hint: params.loginHint }),
    ...params?.customQueryParams,
  };
}

export const Auth0AuthProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const auth0 = useAuth0();

  const value: CommonAuthProviderContextValue = {
    isLoading: auth0.isLoading,
    user: auth0.user,
    isAuthenticated: auth0.isAuthenticated,
    hasIdpManagedPermissions: hasIdpManagedPermissions(auth0.user?.sub),
    hasIdpManagedProfile: hasIdpManagedProfile(auth0.user?.sub),
    oemConnection: parseOemConnection(auth0.user?.sub),
    getAccessToken: (params) => auth0.getAccessTokenSilently({ cacheMode: params?.cacheMode }),
    getIdTokenClaims: async () => auth0.getIdTokenClaims(),
    logout: (params) => auth0.logout({ logoutParams: { returnTo: params.returnTo } }),
    loginWithRedirect: async (params) => {
      const config: RedirectLoginOptions<AppState> = {
        appState: { returnTo: params?.returnTo },
        authorizationParams: auth0AuthorizationParams(params),
      };

      return auth0.loginWithRedirect(config);
    },
  };

  return (
    <CommonAuthProviderContext.Provider value={value}>
      {children}
    </CommonAuthProviderContext.Provider>
  );
};

export const CommonAuthProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const { provider } = useAuthProvider();

  if (provider === 'LOADING') {
    return children;
  }

  const ProviderComponent = provider === 'OIDC' ? GenericAuthProvider : Auth0AuthProvider;

  return <ProviderComponent>{children}</ProviderComponent>;
};

const forwardedParams = [
  'returnTo',
  'loginHint',
] as const satisfies (keyof LoginWithRedirectOptions)[];

export const useCommonAuthProvider = (): CommonAuthProviderContext => {
  const baseContext = useContext(CommonAuthProviderContext);
  const router = useRouter();
  const { cloudAuthNewLoginExperience } = useFlagsProxy();

  const login = useCallback(async (params?: LoginWithRedirectOptions) => {
    if (cloudAuthNewLoginExperience) {
      if (params?.connection || params?.screen_hint === 'signup') {
        // If we already know the intended connection method and/or the user is going directly to signup
        // bypass the new login experience
        return baseContext.loginWithRedirect(params);
      }
      const query = new URLSearchParams();

      for (const param of forwardedParams) {
        if (params?.[param]) {
          query.append(param, params?.[param]);
        }
      }
      router.push('/login/identifier' + (query.size > 0 ? `?${query.toString()}` : ''));

      return;
    }

    return baseContext.loginWithRedirect(params);
  }, [router, baseContext, cloudAuthNewLoginExperience]);

  return {
    ...baseContext,
    login,
  };
};

export const dynamic = 'force-dynamic';
